import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, ChevronLeft, ChevronRight, Users, Upload as UploadIcon, Sparkles } from 'lucide-react';
import PreferencesChips from './PreferencesChips';
import PeopleSuggestions from './PeopleSuggestions';
import api from '../../lib/api';
import { useAnalytics } from '../../analytics';
import {
  setWizardStep,
  setWizardPrefs,
  setWizardDismissedNow,
  markWizardCompleted,
  getWizard
} from '../../lib/onboardingStorage';

/**
 * Multi-step onboarding wizard modal
 * Steps: Welcome → Preferences → Find People → Finish
 */
const OnboardingWizardModal = ({ isOpen, onClose, currentUser }) => {
  const navigate = useNavigate();
  const modalRef = useRef(null);
  const firstFocusableRef = useRef(null);
  const { trackEvent } = useAnalytics();

  // Get initial state from storage
  const wizard = getWizard();
  const [currentStep, setCurrentStep] = useState(wizard.stepIdx || 0);
  const [preferences, setPreferences] = useState(wizard.prefs || []);
  const [suggestedUsers, setSuggestedUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [initialFollowState, setInitialFollowState] = useState({});

  const totalSteps = 4;

  // Sandwich type options (curated list)
  const sandwichTypes = [
    'Reuben',
    'Italian',
    'BLT',
    'Veggie',
    'Breakfast',
    'Cheesesteak',
    'Banh Mi',
    'Wrap',
    'Panini',
    'Grilled Cheese',
    'Club',
    'Meatball'
  ];

  // Track modal open
  useEffect(() => {
    if (isOpen) {
      trackEvent('onboarding_wizard_opened', { stepIdx: currentStep });
      
      // Lock body scroll
      document.body.style.overflow = 'hidden';
      
      // Focus first element
      if (firstFocusableRef.current) {
        firstFocusableRef.current.focus();
      }
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen, currentStep]);

  // Fetch suggested users for step 3
  useEffect(() => {
    if (isOpen && currentStep === 2 && suggestedUsers.length === 0) {
      fetchSuggestedUsers();
    }
  }, [isOpen, currentStep]);

  // Handle ESC key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        handleSkip();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, currentStep]);

  // Focus trap
  useEffect(() => {
    if (!isOpen || !modalRef.current) return;

    const focusableElements = modalRef.current.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    const handleTab = (e) => {
      if (e.key !== 'Tab') return;

      if (e.shiftKey) {
        if (document.activeElement === firstElement) {
          e.preventDefault();
          lastElement?.focus();
        }
      } else {
        if (document.activeElement === lastElement) {
          e.preventDefault();
          firstElement?.focus();
        }
      }
    };

    document.addEventListener('keydown', handleTab);
    return () => document.removeEventListener('keydown', handleTab);
  }, [isOpen, currentStep]);

  const fetchSuggestedUsers = async () => {
    setLoadingUsers(true);
    try {
      const response = await api.get('/onboarding/suggested-users', {
        params: {
          limit: 4
        }
      });
      
      // Filter out current user
      const users = (response.data.users || []).filter(
        u => u.id !== currentUser?.id
      );
      setSuggestedUsers(users);
      
      // Fetch initial follow status for suggested users
      if (users.length > 0) {
        try {
          const userIds = users.map(u => u.id).join(',');
          const followResponse = await api.get(`/following/batch?user_ids=${userIds}`);
          setInitialFollowState(followResponse.data.following_status || {});
        } catch (error) {
          console.error('Error fetching follow status:', error);
          setInitialFollowState({});
        }
      }
    } catch (error) {
      console.error('Error fetching suggested users:', error);
      setSuggestedUsers([]);
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleNext = () => {
    trackEvent('onboarding_wizard_next_clicked', { fromStepIdx: currentStep });
    
    if (currentStep === 1) {
      // Save preferences
      setWizardPrefs(preferences);
      trackEvent('onboarding_prefs_saved', { count: preferences.length });
    }
    
    const nextStep = currentStep + 1;
    setCurrentStep(nextStep);
    setWizardStep(nextStep);
  };

  const handleBack = () => {
    trackEvent('onboarding_wizard_back_clicked', { fromStepIdx: currentStep });
    const prevStep = currentStep - 1;
    setCurrentStep(prevStep);
    setWizardStep(prevStep);
  };

  const handleSkip = () => {
    trackEvent('onboarding_wizard_dismissed', { stepIdx: currentStep });
    setWizardDismissedNow();
    onClose();
  };

  const handleComplete = () => {
    trackEvent('onboarding_wizard_completed', {});
    markWizardCompleted();
    // Persist completion to the server so it never shows again across devices
    (async () => {
      try {
        await api.post('/auth/complete-onboarding');
      } catch (e) {
        // non-blocking
      }
    })();
    onClose();
  };

  const handleFollow = async (userId, shouldFollow) => {
    try {
      if (shouldFollow) {
        await api.post(`/follow/${userId}`);
        trackEvent('onboarding_follow_clicked', { userId });
      } else {
        await api.delete(`/follow/${userId}`);
      }
    } catch (error) {
      console.error('Error following user:', error);
      throw error;
    }
  };

  const handleSeeMorePeople = () => {
    trackEvent('onboarding_see_more_people_clicked', {});
    handleComplete();
    navigate('/search?type=users&sort_by=recent');
  };

  const handleFinishUpload = () => {
    trackEvent('onboarding_finish_cta_clicked', { action: 'upload' });
    handleComplete();
    navigate('/upload');
  };

  const handleFinishFeed = () => {
    trackEvent('onboarding_finish_cta_clicked', { action: 'feed' });
    handleComplete();
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="onboarding-title"
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black bg-opacity-60 backdrop-blur-sm"
        onClick={handleSkip}
        aria-hidden="true"
      />

      {/* Modal Content */}
      <div
        ref={modalRef}
        className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
      >
        {/* Close Button */}
        <button
          ref={firstFocusableRef}
          onClick={handleSkip}
          className="absolute top-4 right-4 z-10 p-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 transition-colors"
          aria-label="Close onboarding"
        >
          <X size={24} aria-hidden="true" />
        </button>

        {/* Step Indicator */}
        <div className="px-8 pt-8 pb-4">
          <div className="flex items-center justify-center gap-2">
            {[...Array(totalSteps)].map((_, idx) => (
              <div
                key={idx}
                className={`h-2 rounded-full transition-all ${
                  idx === currentStep
                    ? 'w-8 bg-passion-red'
                    : idx < currentStep
                    ? 'w-2 bg-passion-red'
                    : 'w-2 bg-gray-300'
                }`}
                aria-label={`Step ${idx + 1} of ${totalSteps}${idx === currentStep ? ' (current)' : idx < currentStep ? ' (completed)' : ''}`}
              />
            ))}
          </div>
          <p className="text-center text-sm text-gray-500 mt-2">
            Step {currentStep + 1} of {totalSteps}
          </p>
        </div>

        {/* Step Content */}
        <div className="px-8 pb-8">
          {/* Step 0: Welcome */}
          {currentStep === 0 && (
            <div className="text-center py-8">
              <div className="mb-6 flex justify-center">
                <div className="w-20 h-20 bg-passion-red bg-opacity-10 rounded-full flex items-center justify-center">
                  <Sparkles size={40} className="text-passion-red" aria-hidden="true" />
                </div>
              </div>
              <h2
                id="onboarding-title"
                className="text-3xl font-bold text-text-black mb-4 gayathri-font"
              >
                Welcome to Club:Sandwich
              </h2>
              <p className="text-gray-600 text-lg mb-2 mplus2-font max-w-md mx-auto">
                Join a community of sandwich lovers discovering, reviewing, and sharing the best sandwiches around the world.
              </p>
              <p className="text-gray-500 text-base mplus2-font max-w-md mx-auto">
                Let's get you started with a quick tour!
              </p>
            </div>
          )}

          {/* Step 1: Sandwich Preferences */}
          {currentStep === 1 && (
            <div className="py-8">
              <div className="mb-6 flex justify-center">
                <div className="w-20 h-20 bg-discovery-gold bg-opacity-20 rounded-full flex items-center justify-center">
                  <span className="text-4xl" role="img" aria-label="Sandwich">🥪</span>
                </div>
              </div>
              <h2
                id="onboarding-title"
                className="text-2xl font-bold text-text-black mb-3 text-center gayathri-font"
              >
                What types of sandwiches do you like?
              </h2>
              <p className="text-gray-600 text-center mb-8 mplus2-font">
                Select all that apply. This helps us personalize your experience.
              </p>
              <PreferencesChips
                options={sandwichTypes}
                value={preferences}
                onChange={setPreferences}
              />
              <p className="text-sm text-gray-400 text-center mt-4">
                {preferences.length === 0
                  ? 'Select at least one to continue, or skip for now'
                  : `${preferences.length} selected`}
              </p>
            </div>
          )}

          {/* Step 2: Find People */}
          {currentStep === 2 && (
            <div className="py-8">
              <div className="mb-6 flex justify-center">
                <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center">
                  <Users size={40} className="text-blue-600" aria-hidden="true" />
                </div>
              </div>
              <h2
                id="onboarding-title"
                className="text-2xl font-bold text-text-black mb-3 text-center gayathri-font"
              >
                Find people to follow
              </h2>
              <p className="text-gray-600 text-center mb-8 mplus2-font">
                Connect with other sandwich enthusiasts and discover their favorite spots.
              </p>
              
              {loadingUsers ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-passion-red mx-auto"></div>
                  <p className="text-gray-500 mt-4">Finding sandwich lovers...</p>
                </div>
              ) : (
                <>
                  <PeopleSuggestions
                    users={suggestedUsers}
                    onFollow={handleFollow}
                    currentUserId={currentUser?.id}
                    initialFollowState={initialFollowState}
                  />
                  
                  <div className="text-center mt-6">
                    <button
                      onClick={handleSeeMorePeople}
                      className="text-passion-red hover:underline text-sm font-medium"
                    >
                      See more people →
                    </button>
                  </div>
                </>
              )}
            </div>
          )}

          {/* Step 3: Finish */}
          {currentStep === 3 && (
            <div className="text-center py-8">
              <div className="mb-6 flex justify-center">
                <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center">
                  <span className="text-4xl" role="img" aria-label="Party">🎉</span>
                </div>
              </div>
              <h2
                id="onboarding-title"
                className="text-3xl font-bold text-text-black mb-4 gayathri-font"
              >
                You're all set!
              </h2>
              <p className="text-gray-600 text-lg mb-8 mplus2-font max-w-md mx-auto">
                Ready to start your sandwich journey? Choose what you'd like to do first:
              </p>
              
              <div className="space-y-4 max-w-md mx-auto">
                <button
                  onClick={handleFinishUpload}
                  className="w-full flex items-center justify-center gap-3 bg-passion-red text-white px-6 py-4 rounded-full hover:bg-red-600 transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-1"
                >
                  <UploadIcon size={20} aria-hidden="true" />
                  <span className="font-semibold text-lg">Upload your first sandwich</span>
                </button>
                
                <button
                  onClick={handleFinishFeed}
                  className="w-full flex items-center justify-center gap-3 border-2 border-passion-red text-passion-red px-6 py-4 rounded-full hover:bg-passion-red hover:text-white transition-all"
                >
                  <span className="font-semibold text-lg">Explore the feed</span>
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Navigation Buttons */}
        {currentStep < 3 && (
          <div className="border-t border-gray-200 px-8 py-6 flex items-center justify-between gap-4">
            {/* Back Button */}
            {currentStep > 0 ? (
              <button
                onClick={handleBack}
                className="flex items-center gap-2 text-gray-600 hover:text-gray-800 font-medium transition-colors"
                aria-label="Go back to previous step"
              >
                <ChevronLeft size={20} aria-hidden="true" />
                <span>Back</span>
              </button>
            ) : (
              <div />
            )}

            {/* Skip / Next Buttons */}
            <div className="flex items-center gap-3">
              <button
                onClick={handleSkip}
                className="px-4 py-2 text-gray-500 hover:text-gray-700 font-medium transition-colors"
              >
                Skip for now
              </button>
              <button
                onClick={handleNext}
                className="flex items-center gap-2 bg-passion-red text-white px-6 py-2 rounded-full hover:bg-red-600 transition-colors font-medium shadow-md"
                aria-label="Go to next step"
              >
                <span>Next</span>
                <ChevronRight size={20} aria-hidden="true" />
              </button>
            </div>
          </div>
        )}

        {/* Final Step Footer */}
        {currentStep === 3 && (
          <div className="border-t border-gray-200 px-8 py-6">
            <button
              onClick={handleSkip}
              className="w-full py-3 text-gray-600 hover:text-gray-800 font-medium transition-colors"
            >
              I'll do this later
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default OnboardingWizardModal;

